/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getTiposDeEmpleados,
  postTipoDeEmpleado,
  deleteTipoDeEmpleado,
  patchTipoDeEmpleado,
  getTipoDeEmpleado,
} from 'api/catalogos/tiposDeEmpleados';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_INACTIVO,
} from 'utils/constants';
import {
  SLUG_PATTERN,
} from 'utils/patterns';

export const tipodeempleadoPermission = {
  permissionModel: 'tipodeempleado',
  catalogsModels: [],
  tabsModels: [],
};

const TipoDeEmpleado = ({ permission }) => {
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [disableAuth, setDisableAuth] = useState(false);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const [visibleAuthConfirmation, setVisibleAuthConfirmation] = useState(false);
  const [visibleAlertInactive, setVisibleAlertInactive] = useState(false);

  const formInstances = [
    form,
    formDatos,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposDeEmpleados();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getTiposDeEmpleados(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    setDisableForm(false);
    form.resetFields();
    formDatos.resetFields();
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchTipoDeEmpleado(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleAuthConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const isDisabled = (rowEstatus) => {
    if (rowEstatus !== ESTATUS_ACTIVO) {
      setDisableForm(true);
    }
  };
  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _comprobanteDeEmpleado = await getTipoDeEmpleado(match.id);
    setTimeout(() => {
      if (_comprobanteDeEmpleado) {
        form.resetFields();
        formDatos.resetFields();
        setTimeout(() => {
          isDisabled(_comprobanteDeEmpleado?.estados_globales);
          form.setFieldsValue(formatReceived(_comprobanteDeEmpleado));
          formDatos.setFieldsValue(formatReceived(_comprobanteDeEmpleado));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDatos.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formDatos.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchTipoDeEmpleado(key, values) : postTipoDeEmpleado(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteTipoDeEmpleado(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    setDeleteDisabled(isAutorizado);
    setDisableAuth(isAutorizado || isInactivo);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 400,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 400,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    concepto: [requiredRule],
    clave: [
      requiredRule,
      {
        pattern: SLUG_PATTERN,
        message: 'Ingrese una clave válida',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          customActions={[
            {
              onClick: () => setVisibleAuthConfirmation(true),
              permission: { model: 'tipodeempleado', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              // eslint-disable-next-line max-len
              disabled: !selectedRowKeys?.length || disableAuth,
            },
          ]}
          disableDelete={
            deleteDisabled
          }
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Empleado`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!selectedRowKeys?.length || !disableAuth}
            />
            )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            disabled={disableForm}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="clave"
                rules={rules.clave}
                label="Clave"
                hasFeedback
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="descripcion"
                rules={rules.required}
                label="Descripción"
                hasFeedback
              >
                <Input.TextArea
                  allowClear
                  autoSize={{ minRows: 2, maxRows: 3 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.required}
                hasFeedback
                initialValue={ESTATUS_ACTIVO}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  activeInactive={!deleteDisabled}
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                  onChange={(val) => {
                    if (val === ESTATUS_INACTIVO) {
                      setVisibleAlertInactive(true);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Tipo de Empleado ${form.getFieldValue('clave')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleAlertInactive(false)}
          ConfirmInactive
          onCancel={() => {
            setVisibleAlertInactive(false);
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
          }}
          open={visibleAlertInactive}
          contentTitle={`Inactivar el Tipo de Empleado ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setVisibleAuthConfirmation(false);
            onCancel();
          }}
          open={visibleAuthConfirmation}
          contentTitle={`Autorizar el Tipo de Empleado ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TipoDeEmpleado;
